import { useEffect } from 'react';
import styles from './Toast.module.scss';
import toastUI from 'react-hot-toast';
import headlessToast, {
	useToaster,
	Toast as ToastType,
	Renderable,
	ValueOrFunction,
	ToastOptions
} from 'react-hot-toast/headless';
import {
	SuccessChip,
	ErrorChip,
	WarnChip,
	InfoChip
} from '@components';
import { useLocation } from 'react-router-dom';
import { toast as ToastMain } from 'react-hot-toast';

type Message = ValueOrFunction<Renderable, ToastType | 'info' | 'warn'>;
type ExtendedToastOptions = ToastOptions & {
	title?: string
	actionButtons?: (null | {
		text: string;
		icon?: React.ComponentType<{ [key: string]: string }>;
		onClick: () => void;
	})[];
	keepToastWhenLocationChange?: boolean;
};
type ToastHandler = (message: Message, options?: ExtendedToastOptions) => string;

interface hToastType extends ToastType {
	info: ToastHandler;
	warn: ToastHandler;
	error: ToastHandler;
	success: ToastHandler;
	dismiss(toastId?: string): void;
	remove(toastId?: string): void;
}

export const ToastUI = () => {
	const { toasts } = useToaster();
	const location = useLocation();

	useEffect(() => {
		toasts.forEach((t) => {
			const opts = t as unknown as ExtendedToastOptions;
			// If the toast is NOT marked to persist, dismiss it on route change
			if (!opts.keepToastWhenLocationChange) {
				ToastMain.remove(t.id);
			}
		});
	}, [location]);

	useEffect(() => {
		toasts.filter((toast) => toast.visible && !toast.dismissed).map((t) => {
			const opts = t as unknown as ExtendedToastOptions;

			switch (t.type) {
			case 'success':
				return toastUI(
					<SuccessChip
						text={(t.message as string)}
						showCloseButton
						onClose={() => ToastMain.dismiss(t.id)}
						actionButtons={opts.actionButtons}
						isToast
						title={opts.title}
					/>,
					{
						className: styles.toast,
						id: t.id,
						duration: t.duration || 3500,
						position: t.position,
					}
				);
			case 'error':
				return toastUI(
					<ErrorChip
						text={(t.message as string)}
						showCloseButton
						onClose={() => ToastMain.dismiss(t.id)}
						actionButtons={opts.actionButtons}
						isToast
						title={opts.title}
					/>, {
						className: styles.toast,
						id: t.id,
						duration: t.duration || 3500,
						position: t.position
					});
			default:
				return toastUI(t.message);
			}
		});
	}, [toasts]);

	return <div></div>
}

(headlessToast as unknown as hToastType).info = (message: Message, opts?: ExtendedToastOptions) => {
	return toastUI(
		<InfoChip
			text={(message as string)}
			showCloseButton
			onClose={() => ToastMain.dismiss(opts?.id)}
			actionButtons={opts?.actionButtons}
			isToast
			title={opts?.title}
		/>, {
			className: styles.toast,
			id: opts?.id,
			duration: opts?.duration || 3500,
			position: opts?.position
		});
}
(headlessToast as unknown as hToastType).warn = (message: Message, opts?: ExtendedToastOptions) => {
	return toastUI(
		<WarnChip
			text={(message as string)}
			showCloseButton
			onClose={() => ToastMain.dismiss(opts?.id)}
			isToast
			title={opts?.title}
		/>, {
			className: styles.toast,
			id: opts?.id,
			duration: opts?.duration || 3500,
			position: opts?.position
		});
}

export const toast = headlessToast as unknown as hToastType; //eslint-disable-line
